import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAllMascots, getUserMascot, chooseMascot, Mascot, UserMascot, canChangeMascot } from '../lib/gamification/mascotService';
import { getUserGamificationStatus } from '../lib/gamification/gamificationService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, Star } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';

const ChooseMascotPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mascots, setMascots] = useState<Mascot[]>([]);
  const [loading, setLoading] = useState(true);
  const [userMascot, setUserMascot] = useState<UserMascot | null>(null);
  const [userLevel, setUserLevel] = useState<number>(1);
  const [choosing, setChoosing] = useState<string | null>(null);
  const [canChange, setCanChange] = useState(true);
  const [remainingChanges, setRemainingChanges] = useState(3);
  const [lastResetLevel, setLastResetLevel] = useState(1);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!user) return;
      try {
        const mascotsList = await getAllMascots(supabase);
        setMascots(mascotsList);
        const status = await getUserGamificationStatus(supabase, user.id);
        setUserLevel(status?.current_level || 1);
        const mascot = await getUserMascot(supabase, user.id, status?.current_level || 1);
        setUserMascot(mascot);
        setCheckingLimit(true);
        const limit = await canChangeMascot(supabase, user.id, status?.current_level || 1);
        setCanChange(limit.canChange);
        setRemainingChanges(limit.remaining);
        setLastResetLevel(limit.lastResetLevel);
        setCheckingLimit(false);
        // Extrair categorias únicas
        const cats = Array.from(new Set(mascotsList.map(m => m.category).filter(Boolean)));
        setAvailableCategories(cats as string[]);
      } catch (err) {
        toast.error('Erro ao carregar mascotes.');
        setCheckingLimit(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleChooseMascot = async (mascotId: string) => {
    if (!user) return;
    setChoosing(mascotId);
    try {
      const result = await chooseMascot(supabase, user.id, mascotId, userLevel);
      if (result.success) {
        toast.success('Mascote escolhido com sucesso!');
        // Atualiza mascote do usuário e limite
        const status = await getUserGamificationStatus(supabase, user.id);
        const mascot = await getUserMascot(supabase, user.id, status?.current_level || 1);
        setUserMascot(mascot);
        const limit = await canChangeMascot(supabase, user.id, status?.current_level || 1);
        setCanChange(limit.canChange);
        setRemainingChanges(limit.remaining);
        setLastResetLevel(limit.lastResetLevel);
      } else {
        toast.error(result.error || 'Erro ao escolher mascote.');
      }
    } catch (err) {
      toast.error('Erro ao escolher mascote.');
    } finally {
      setChoosing(null);
    }
  };

  // Filtro de mascotes
  const filteredMascots = mascots.filter(m => {
    const matchName = m.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !category || m.category === category;
    return matchName && matchCategory;
  });

  return (
    <div className={`container mx-auto p-4 max-w-3xl ${theme === 'dark' ? 'text-foreground' : 'bg-gray-50 text-gray-900'}`}>
      <Button asChild variant="outline" className="mb-4" size="sm">
        <Link to="/profile/xp-history">← Voltar para Meu Progresso</Link>
      </Button>
      <h1 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Escolha seu Mascote</h1>
      <p className={`mb-6 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>
        Selecione um mascote para te acompanhar na sua jornada. Veja como ele evolui conforme seu nível!
      </p>
      <div className={`mb-4 p-3 border rounded ${theme === 'dark' ? 'bg-blue-00/30 border-blue-700/50' : 'bg-blue-50 border-blue-200'}`}>
        <div className={`font-semibold mb-1 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'}`}>Como funciona a troca de mascote?</div>
        <div className={`text-sm mb-1 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-800'}`}>
          Você pode trocar de mascote até <b>3 vezes por ano</b> <b>ou</b> a cada <b>20 níveis</b> que subir. Assim que atingir um novo ciclo de 20 níveis, seu limite de trocas é renovado automaticamente!
        </div>
        <div className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'}`}>
          <b>Trocas restantes:</b> {remainingChanges} de 3
          {userLevel && lastResetLevel !== undefined && (
            <>
              {userLevel - lastResetLevel < 20 && (
                <span className={`ml-2 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-700'}`}>• Faltam <b>{20 - (userLevel - lastResetLevel)}</b> níveis para o próximo reset</span>
              )}
            </>
          )}
        </div>
      </div>
      {checkingLimit ? (
        <div className={`flex items-center gap-2 text-sm mb-4 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}><Loader2 className="h-4 w-4 animate-spin" /> Verificando limite de trocas...</div>
      ) : (
        <div className="mb-4 text-sm">
          {canChange ? (
            <span className={`${theme === 'dark' ? 'text-green-400' : 'text-green-700'}`}>Você pode trocar de mascote <b>{remainingChanges}</b> vez(es) até o próximo reset.</span>
          ) : (
            <span className={`${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>Limite de trocas atingido. Você poderá trocar novamente ao subir 20 níveis (próximo reset: nível {lastResetLevel + 20}) ou no próximo ano.</span>
          )}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <input
          type="text"
          placeholder="Buscar mascote por nome..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`border rounded px-3 py-2 w-full md:w-64 ${theme === 'dark' ? 'bg-input border-border placeholder:text-muted-foreground text-foreground focus:ring-ring' : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'}`}
        />
        <select
          value={category || ''}
          onChange={e => setCategory(e.target.value || null)}
          className={`border rounded px-3 py-2 w-full md:w-48 ${theme === 'dark' ? 'bg-input border-border text-foreground focus:ring-ring' : 'border-gray-300 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500'}`}
        >
          <option value="">Todas as categorias</option>
          {availableCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-40"><Loader2 className={`h-8 w-8 animate-spin ${theme === 'dark' ? 'text-primary' : 'text-indigo-600'}`} /></div>
      ) : (
        <div className="space-y-4">
          {filteredMascots.length === 0 ? (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>Nenhum mascote encontrado.</div>
          ) : (
            filteredMascots.map((mascot) => {
              const isCurrent = userMascot?.mascot.id === mascot.id;
              return (
                <div key={mascot.id} className={`flex flex-col sm:flex-row items-center gap-4 p-4 border rounded-lg shadow-sm ${theme === 'dark' ? 'bg-gray-800 border-border' : 'bg-white border-gray-200'}`}>
                  {/* Imagem do mascote */}
                  <img src={mascot.levels[0]?.image_url} alt={mascot.name} className={`w-20 h-20 rounded-full object-cover border ${theme === 'dark' ? 'border-border' : 'border-gray-300'}`} />
                  {/* Info principal */}
                  <div className="flex-1 flex flex-col items-start">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-lg ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-900'}`}>{mascot.name}</span>
                      {mascot.category && (
                        <span className={`text-xs px-2 py-0.5 rounded ml-2 ${theme === 'dark' ? 'bg-accent text-accent-foreground' : 'bg-blue-100 text-blue-800'}`}>{mascot.category}</span>
                      )}
                      {isCurrent && <span title="Mascote atual"><CheckCircle2 className={`h-5 w-5 ml-2 ${theme === 'dark' ? 'text-primary' : 'text-blue-500'}`} /></span>}
                    </div>
                    {/* Botão de escolher/trocar */}
                    <Button
                      size="sm"
                      className="mt-2"
                      disabled={isCurrent || !canChange}
                      onClick={() => handleChooseMascot(mascot.id)}
                      variant={isCurrent ? "secondary" : "default"}
                    >
                      {choosing === mascot.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {isCurrent ? "Mascote atual" : canChange ? "Escolher mascote" : "Limite de trocas atingido"}
                    </Button>
                    {/* Descrição */}
                    {mascot.description && (
                      <div className={`text-sm mt-2 max-w-xl ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-700'}`}>{mascot.description}</div>
                    )}
                  </div>
                  {/* Níveis do mascote */}
                  <div className="flex flex-col items-center gap-1 min-w-[120px]">
                    <div className={`font-semibold text-xs mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>Evoluções</div>
                    <div className="flex flex-row gap-2">
                      {mascot.levels.map((level) => (
                        <div key={level.id} className="flex flex-col items-center">
                          <img src={level.image_url} alt={`Nível ${level.level_number}`} className={`w-10 h-10 rounded-full border mb-1 ${theme === 'dark' ? 'border-border' : 'border-gray-300'}`} />
                          <span className={`text-xs ${theme === 'dark' ? 'text-card-foreground' : 'text-gray-800'}`}>Nível {level.level_number}</span>
                          <span className={`text-[10px] ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-400'}`}>Lv. {level.min_user_level}</span>
                          {userLevel >= level.min_user_level && (
                            <span title="Você já desbloqueou este nível"><Star className="h-4 w-4 text-yellow-400 mt-1" /></span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ChooseMascotPage; 